import {
  AccountsTypeaheadDocument,
  AccountsTypeaheadQuery,
  IssuersTypeaheadDocument,
  IssuersTypeaheadQuery,
  OmnisearchDocument,
  OmnisearchQuery,
  OmnisearchQueryVariables,
  OpportunitiesTypeaheadDocument,
  OpportunitiesTypeaheadQuery,
  OpportunitiesTypeaheadQueryVariables,
} from 'graphql/bindings/AdminBindings'
import { AdminClient } from 'graphql/clients'

// debounce from lodash won't work as intended for the promises below because on the
// second and subsequent calls, it returns the previous value (typeaheads will appear
// one keystroke behind users' intent)
import debouncePromise from 'debounce-promise'

// this value is used it common among typeahead debounce definitions to keep interaction
// with typeaheads consistent
const debounceDelay = 250

const resolveAccounts = debouncePromise(
  (inputVal: string) =>
    AdminClient.query<AccountsTypeaheadQuery>({
      query: AccountsTypeaheadDocument,
      variables: { query: inputVal },
      fetchPolicy: 'network-only',
    }).then(result =>
      result.data.accountsTypeahead.map(elem => {
        const differentiator =
          elem.type === 'individual'
            ? (elem.email && ` <${elem.email}>`) || ''
            : (elem.client_type && ` <${elem.client_type}>`) || ''

        return { label: elem.name + differentiator, value: elem.id }
      })
    ),
  debounceDelay
)

const resolveIssuers = debouncePromise(
  (inputVal: string) =>
    AdminClient.query<IssuersTypeaheadQuery>({
      query: IssuersTypeaheadDocument,
      variables: { query: inputVal },
      fetchPolicy: 'network-only',
    }).then(result =>
      result.data.accountsTypeahead.map(elem => {
        const domain = elem?.issuer_account?.company?.domain ?? ''
        return { label: elem.name + (domain && ` <${domain}>`), value: elem.id }
      })
    ),
  debounceDelay
)

const resolveOmnisearch = debouncePromise((inputVal: string) => {
  return AdminClient.query<OmnisearchQuery, OmnisearchQueryVariables>({
    query: OmnisearchDocument,
    variables: { query: inputVal, limit: 3 },
    fetchPolicy: 'network-only',
  }).then(result => {
    const searchResults = result.data.accountSearch
    // react-select hides empty groups... we want to show them so use this 'empty' hack
    const orgResults = searchResults.organization_accounts.length > 0 ? searchResults.organization_accounts : ['empty']
    const indResults = searchResults.individual_accounts.length > 0 ? searchResults.individual_accounts : ['empty']
    const companyResults = searchResults.issuer_accounts.length > 0 ? searchResults.issuer_accounts : ['empty']

    return [
      { label: 'Organizations', options: orgResults },
      { label: 'Individuals', options: indResults },
      { label: 'Issuers', options: companyResults },
    ]
  })
}, debounceDelay)

const resolveOpportunities = (accountId: string) =>
  debouncePromise(
    (query: string) =>
      AdminClient.query<OpportunitiesTypeaheadQuery, OpportunitiesTypeaheadQueryVariables>({
        query: OpportunitiesTypeaheadDocument,
        variables: { accountId, query },
        fetchPolicy: 'network-only',
      }).then(result =>
        result.data.opportunitiesTypeahead.map(elem => ({
          label: `${elem.company.name} [${elem.trade_intent.type}]`,
          value: elem.id,
        }))
      ),
    debounceDelay
  )

export { resolveAccounts, resolveIssuers, resolveOpportunities, resolveOmnisearch }
